import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useBattleStore } from "../store/useBattleStore";
import { useCampaignRunStore } from "../store/useCampaignRunStore";
import { useGameStore } from "../store/useGameStore";
import { useWorldBossStore } from "../store/useWorldBossStore";
import { useCatacombsStore } from "../store/useCatacombsStore";
import { useArenaStore } from "../store/useArenaStore";
import { worldBossApi } from "../api/worldBoss";
import { hapticSuccess, hapticError } from "../utils/telegram";
import { energyCostLabel, hasEnergyFor } from "../utils/assets";
import { BattleArena } from "../components/BattleArena";
import { ResultOverlay } from "../components/ResultOverlay";
import { AutoBattleModeModal } from "../components/AutoBattleModeModal";
import { replayDelayMs } from "../hooks/useBattleReplay";
import type { AutoBattleMode, BattleLogEvent, BattleType } from "../types";
import { resolveAutoBattlePrefs, resolveAutoBattleEntitlements, isAutoBattleModeAllowed } from "../utils/autoBattle";

const REPLAY_TAIL_POLL_MS = 700;

function resolveBattleContextLabel(
  battle: NonNullable<ReturnType<typeof useBattleStore.getState>["battle"]>,
): string | undefined {
  switch (battle.battle_type) {
    case "CATACOMBS": {
      const catacombs = useCatacombsStore.getState().state;
      const preview = catacombs?.next_preview;
      if (preview) {
        return preview.is_boss ? `Floor ${preview.floor} · Boss` : `Floor ${preview.floor}`;
      }
      const next = (catacombs?.highest_cleared ?? 0) + 1;
      return next > 0 ? `Floor ${next}` : "Catacombs";
    }
    case "WORLD_BOSS":
      return useWorldBossStore.getState().status?.boss_name ?? "World Boss";
    case "PVP":
    case "ARENA_SOLO":
      return battle.rewards?.opponent_name ? `vs ${battle.rewards.opponent_name}` : "Arena Duel";
    case "ARENA_PARTY":
      return "Arena Party";
    case "CAMPAIGN":
      return useCampaignRunStore.getState().run?.stage_name ?? "Campaign";
    case "PVE":
      return "Training Ground";
    default:
      return undefined;
  }
}

function resolveViewerAllyTeam(log: BattleLogEvent[], viewerUid?: string): 0 | 1 {
  if (!viewerUid) return 0;
  const init = log.find((e) => e.action === "INIT");
  const self = init?.units?.find((u) => u.uid === viewerUid);
  return self?.team === 1 ? 1 : 0;
}

export function BattleScreen() {
  const { battle, phase, visibleLog, replayNextEvent, startBattle, claimRewards, finalizePreclaimedBattle, reset, error, droppedItems } =
    useBattleStore();
  const { hero, user, refreshHero, refreshBalance, setScreen, checkLevelUp, enqueueLoot } = useGameStore();
  const { myPartyRoom } = useArenaStore();
  const { isRunLocked, run, syncActiveRun } = useCampaignRunStore();

  const replayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [dismissingResult, setDismissingResult] = useState(false);
  const [pendingBattle, setPendingBattle] = useState<{ type: BattleType } | null>(null);
  const [pickerMode, setPickerMode] = useState<AutoBattleMode>("rotation");

  const autoBattlePrefs = resolveAutoBattlePrefs(hero);
  const autoBattleEntitlements = resolveAutoBattleEntitlements(hero);

  const requestBattle = (type: BattleType) => {
    if (autoBattlePrefs.prompt_on_start !== false) {
      setPickerMode(autoBattlePrefs.mode);
      setPendingBattle({ type });
      return;
    }
    void startBattle(type, undefined, autoBattlePrefs.mode);
  };

  const battleModeModal =
    pendingBattle && phase === "idle" ? (
      <AutoBattleModeModal
        hero={hero}
        modeEntitlements={autoBattleEntitlements}
        onNeedPremium={() => setScreen("premium")}
        selected={pickerMode}
        onClose={() => setPendingBattle(null)}
        onSelect={(mode) => {
          if (!isAutoBattleModeAllowed(mode, autoBattleEntitlements)) return;
          const battleType = pendingBattle.type;
          setPendingBattle(null);
          void startBattle(battleType, undefined, mode);
        }}
      />
    ) : null;

  useEffect(() => {
    if (phase === "animating" || phase === "idle" || phase === "starting") {
      setDismissingResult(false);
    }
  }, [phase]);

  useEffect(() => {
    if (isRunLocked) {
      setScreen("campaignRun");
      return;
    }
    void syncActiveRun().then((locked) => {
      if (locked) setScreen("campaignRun");
    });
  }, [isRunLocked, setScreen, syncActiveRun]);

  /* auto-replay — same batched mob hits + short delays as campaign */
  useEffect(() => {
    if (phase !== "animating") return;

    let cancelled = false;

    const scheduleNext = () => {
      if (cancelled) return;
      const { battle, phase: currentPhase } = useBattleStore.getState();
      if (currentPhase !== "animating" || !battle?.log) return;

      replayNextEvent();

      const state = useBattleStore.getState();
      if (state.phase !== "animating") return;

      const log = state.battle!.log!;
      const idx = state.replayIndex;
      const delay = idx < log.length ? replayDelayMs(log[idx], log) : REPLAY_TAIL_POLL_MS;
      replayTimer.current = setTimeout(scheduleNext, delay);
    };

    const { battle, replayIndex } = useBattleStore.getState();
    const initialDelay =
      battle?.log && replayIndex < battle.log.length
        ? replayDelayMs(battle.log[replayIndex], battle.log)
        : REPLAY_TAIL_POLL_MS;
    replayTimer.current = setTimeout(scheduleNext, initialDelay);

    return () => {
      cancelled = true;
      if (replayTimer.current) clearTimeout(replayTimer.current);
    };
  }, [phase, replayNextEvent]);

  /* haptic on result */
  useEffect(() => {
    if (phase === "results" && battle?.result) {
      if (battle.result === "WIN") hapticSuccess();
      else hapticError();
    }
  }, [phase, battle?.result]);

  /* refresh after claim (background sync while result overlay is still open) */
  useEffect(() => {
    if (phase === "claimed") {
      void refreshHero();
      void refreshBalance();
    }
  }, [phase, refreshHero, refreshBalance]);

  /* ── IDLE: battle type selector ── */
  if (phase === "idle") {
    if (isRunLocked && run?.status === "ACTIVE") {
      return (
        <>
          <div className="p-4 space-y-4">
            <h2 className="text-xl font-bold font-game text-center text-gold-400">📖 Campaign</h2>
            <p className="text-gray-400 text-center text-sm">
              Your hero is in «{run.stage_name}». Other battles are unavailable until the run finishes.
            </p>
            <button
              type="button"
              onClick={() => setScreen("campaignRun")}
              className="w-full bg-kingdom-purple border border-kingdom-glow rounded-xl p-4 text-center hover:border-kingdom-glow active:scale-95 transition-all"
            >
              <div className="text-4xl mb-2">🗺️</div>
              <div className="font-bold text-white">Continue campaign</div>
            </button>
          </div>
          {battleModeModal}
        </>
      );
    }

    return (
      <>
      <div className="p-4 space-y-4">
        <h2 className="text-xl font-bold font-game text-center text-gold-400">⚔️ Battle</h2>
        <p className="text-gray-400 text-center text-sm">Choose your battle</p>
        <div className="grid grid-cols-2 gap-3">
          <BattleTypeCard
            icon="🐉"
            title="PvE"
            subtitle={`Fight monsters · ${energyCostLabel(hero, "PVE") || "⚡ 10"}`}
            disabled={!hasEnergyFor(hero, "PVE")}
            onClick={() => requestBattle("PVE")}
          />
          <BattleTypeCard
            icon="🏆"
            title="Arena"
            subtitle={`Earn diamonds · ${energyCostLabel(hero, "ARENA") || "⚡ 15"}`}
            disabled={!hasEnergyFor(hero, "ARENA")}
            onClick={() => requestBattle("ARENA")}
          />
        </div>
        <BattleTypeCard
          icon="🤺"
          title="Duels"
          subtitle={`PvP 1×1 · ${energyCostLabel(hero, "PVP") || "⚡ 12"}`}
          disabled={!hasEnergyFor(hero, "PVP")}
          onClick={() => setScreen("duels")}
        />
        <BattleTypeCard
          icon="📖"
          title="Campaign"
          subtitle="Chapters with branching dungeon paths"
          onClick={() => setScreen("campaign")}
        />
        <BattleTypeCard
          icon="🏰"
          title="MP Dungeons"
          subtitle="Party 1–4 · daily limit"
          onClick={() => setScreen("mpDungeons")}
        />
        {error && (
          <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">{error}</div>
        )}
      </div>
      {battleModeModal}
      </>
    );
  }

  /* ── STARTING: spinner ── */
  if (phase === "starting") {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-0 space-y-4">
        <div className="text-6xl animate-bounce">⚔️</div>
        <p className="text-kingdom-glow font-bold animate-pulse font-game">Simulating battle...</p>
      </div>
    );
  }

  /* ── ARENA ── */
  const viewerPlayerUid = user ? `player_${user.id}` : undefined;
  const isPartyArena = battle?.battle_type === "ARENA_PARTY";
  const isPartyLeader = isPartyArena && myPartyRoom?.leader_id === user?.id;
  const partyAllyTeam = isPartyArena ? resolveViewerAllyTeam(visibleLog, viewerPlayerUid) : 0;

  return (
    <div className="relative h-full min-h-0">
      <BattleArena
        visibleLog={visibleLog}
        heroClass={hero?.hero_class}
        heroName={hero?.name ?? hero?.hero_class}
        fallbackMaxHp={hero?.stats.hp ?? 1000}
        plannedRotation={hero?.rotation}
        battleType={battle?.battle_type}
        battleContextLabel={battle ? resolveBattleContextLabel(battle) : undefined}
        viewerPlayerUid={isPartyArena ? viewerPlayerUid : undefined}
        allyTeam={isPartyArena ? partyAllyTeam : 0}
        opponentFallback={
          (battle?.battle_type === "PVP" || battle?.battle_type === "ARENA_SOLO") &&
          battle.rewards?.opponent_name
            ? {
                displayName: battle.rewards.opponent_name,
                heroClass: battle.rewards.opponent_class ?? "WARRIOR",
                gender: battle.rewards.opponent_gender ?? "m",
                isPlayer: battle.battle_type === "PVP" || battle.battle_type === "ARENA_SOLO",
              }
            : undefined
        }
      />

      {/* ── Result overlay ── */}
      {(phase === "results" || phase === "claimed") && battle && !dismissingResult && (
        <ResultOverlay
          isWin={battle.result === "WIN"}
          rewards={
            battle.status === "CLAIMED" &&
            (battle.battle_type === "WORLD_BOSS" || battle.battle_type === "CATACOMBS")
              ? null
              : battle.rewards
          }
          saving={phase === "results" && battle.status !== "CLAIMED"}
          autoDismissSeconds={isPartyArena ? 3 : undefined}
          buttonLabel={battle.battle_type === "CAMPAIGN" ? "Back to Campaign" : "OK"}
          onContinue={async () => {
            if (dismissingResult) return;
            setDismissingResult(true);

            let loot = droppedItems;
            if (phase === "results") {
              if (battle.status === "CLAIMED") {
                finalizePreclaimedBattle();
              } else {
                loot = await claimRewards();
              }
            }
            if (
              battle.battle_type === "WORLD_BOSS" &&
              (battle.rewards as { player_died?: boolean } | undefined)?.player_died
            ) {
              await worldBossApi.startDeathCooldown();
            }
            await refreshHero();
            await refreshBalance();
            reset();
            if (loot.length) enqueueLoot(loot);
            checkLevelUp();
            if (battle.battle_type === "CAMPAIGN") setScreen("campaign");
            else if (battle.battle_type === "WORLD_BOSS") {
              await useWorldBossStore.getState().pollStatus();
              await useWorldBossStore.getState().refreshLeaderboard();
              setScreen("worldBoss");
            } else if (battle.battle_type === "CATACOMBS") {
              await useCatacombsStore.getState().refresh();
              setScreen("catacombs");
            } else if (battle.battle_type === "ARENA_SOLO") {
              useArenaStore.getState().setArenaTab("solo");
              setScreen("arena");
            } else if (battle.battle_type === "ARENA_PARTY") {
              const arena = useArenaStore.getState();
              arena.setArenaTab("party");
              if (battle.id) {
                useArenaStore.setState({ partyBattleSeenId: battle.id });
              }
              if (isPartyLeader) {
                await arena.ackPartyBattle();
              }
              setScreen("arena");
              void arena.pollParty();
            }
          }}
        />
      )}

      {/* ── Error toast ── */}
      {error && (
        <div className="absolute bottom-20 inset-x-4 bg-rose-900/90 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm text-center z-50">
          {error}
        </div>
      )}
    </div>
  );
}

/* ─── Battle type card ──────────────────────────────────────────────────── */
function BattleTypeCard({
  icon,
  title,
  subtitle,
  onClick,
  disabled,
}: {
  icon: string;
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        "bg-kingdom-purple border border-kingdom-light rounded-xl p-4 text-center transition-all w-full",
        disabled
          ? "opacity-50 cursor-not-allowed"
          : "hover:border-kingdom-glow active:scale-95",
      )}
    >
      <div className="text-4xl mb-2">{icon}</div>
      <div className="font-bold text-white">{title}</div>
      <div className="text-gray-400 text-xs">{subtitle}</div>
    </button>
  );
}
